'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState, type ReactNode } from 'react';

export function APIProvider({ children }: { children: ReactNode }) {
  // Create the QueryClient instance on the client side
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes - garbage collection after 10 minutes
            retry: (_failureCount: number, error: unknown) => {
              // Don't retry on auth and authorization errors
              if (
                error instanceof Error &&
                (error.message.includes('401') || error.message.includes('403'))
              ) {
                return false;
              }
              // Don't retry on AppError with 401 or 403 status codes
              if (error && typeof error === 'object' && 'statusCode' in error) {
                const statusCode = (error as { statusCode: number }).statusCode;
                if (statusCode === 401 || statusCode === 403) {
                  return false;
                }
              }
              return _failureCount < 3;
            },
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
            refetchOnWindowFocus: false, // Don't refetch on window focus
            refetchOnMount: false, // Don't refetch on mount if data exists
            refetchOnReconnect: 'always', // Always refetch when reconnecting
            networkMode: 'online' // Only run queries when online
          },
          mutations: {
            retry: (_failureCount: number, error: unknown) => {
              // Don't retry mutations on auth and authorization errors
              if (
                error instanceof Error &&
                (error.message.includes('401') || error.message.includes('403'))
              ) {
                return false;
              }
              // Don't retry on AppError with 401 or 403 status codes
              if (error && typeof error === 'object' && 'statusCode' in error) {
                const statusCode = (error as { statusCode: number }).statusCode;
                if (statusCode === 401 || statusCode === 403) {
                  return false;
                }
              }
              return false; // Don't retry mutations by default
            },
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
            networkMode: 'online'
          }
        }
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
